import React, { useRef, useState } from 'react';
import { includes, truncate } from 'lodash-es';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import config from 'util/site_config';
import NumberHelper from 'util/number_helper';
import PublicationStatusFlags from 'components/Shared/PublicationStatusFlags';
import ResearchOutput from 'components/Explorer/models/research_output';
import withFeatureFlags from 'components/Flipper/withFeatureFlags';
import * as QueryParams from 'components/Explorer/models/query_params';
import { defaultSearchScope } from '../../util/default_search_scope';
import useQueryParams from 'hooks/useQueryParams';

const SECTIONS = [
  'Authors',
  'Departments',
  'FieldsOfResearch',
  'Affiliations',
  'SustainableDevelopmentGoals',
  'Grants',
  'Researchers'
];

const initialToggleState = () =>
  SECTIONS.reduce((acc, name) => {
    acc[`showToggle${name}`] = false;
    acc[`collapsed${name}`] = true;
    return acc;
  }, {});

const collapsableElement = (el) => !!el && el.scrollHeight > el.clientHeight;

const ResearchOutputItem = ({ output, onBadgeClick, featureFlags = [] }) => {
  const params = useQueryParams();
  const [toggles, setToggles] = useState(initialToggleState);
  const measuredRef = useRef({});

  const view = params.view;

  const setShowToggle = (name, value) => {
    setToggles((prev) => (prev[`showToggle${name}`] === value ? prev : { ...prev, [`showToggle${name}`]: value }));
  };

  const setCollapsed = (name, value) => {
    setToggles((prev) => ({ ...prev, [`collapsed${name}`]: value }));
  };

  const onClick = (event) => {
    event.preventDefault();
    onBadgeClick(output);
  };

  const sourceOrMentionSort =
    includes(config.sources, params.sortOrder) ||
    includes(config.otherSources, params.sortOrder) ||
    includes(config.mentionTimeframes, params.sortOrder);

  const outputTitleLength = (() => {
    if (view === 'grid') return 80;
    if (output.departments.length > 0 || output.authors.length > 0) return 100;
    return 250;
  })();

  const outputTitle = truncate(output.title, { length: outputTitleLength, separator: /\W/, omission: '…' });

  const affiliationSearchLink = (filters) => {
    const scopeFilter = { scope: params.filters.scope || defaultSearchScope(window.current_user) };
    const next = QueryParams.withFilters(params, { ...filters, ...scopeFilter });
    return { search: QueryParams.searchFor(next) };
  };

  const toggle = (name) => {
    if (!toggles[`showToggle${name}`]) return null;
    return (
      <button
        type="button"
        className="truncated-toggle"
        onClick={() => setCollapsed(name, !toggles[`collapsed${name}`])}
      >
        {I18n.t(`Explorer.Outputs.ResearchOutputItem.show_${toggles[`collapsed${name}`] ? 'more' : 'less'}`)}
      </button>
    );
  };

  const displayToggleRef = (name, len) => (el) => {
    if (!el || measuredRef.current[name]) return;
    measuredRef.current[name] = true;
    setShowToggle(name, len && collapsableElement(el));
  };

  const chipsSection = (name, items, renderLink, { className, i18nKey } = {}) => {
    if (view !== 'list') return null;
    if (!items?.length) return null;
    const cls = className || name.toLowerCase();
    const key = i18nKey || `${cls}_html`;
    return (
      <div className={cls}>
        <span className="section-title">
          {I18n.htmlSafe(`Explorer.Outputs.ResearchOutputItem.${key}`, { count: items.length })}
          {toggle(name)}
        </span>
        <div
          ref={displayToggleRef(name, items.length)}
          className={toggles[`collapsed${name}`] ? 'chips' : 'chips expanded'}
        >
          {items.map(renderLink)}
        </div>
      </div>
    );
  };

  const sortContext = sourceOrMentionSort ? (
    <div className="context">
      <span className={`icon ${params.sortOrder}`} />
      <span className="count" data-testid="count">
        {NumberHelper.formatNumberWithDelimiter(output.countForSort)}
      </span>
      <span className="type">
        {I18n.t(params.sortOrder, {
          scope: 'Explorer.Outputs.ResearchOutputItem.sort_option_descriptions',
          count: output.countForSort
        })}
      </span>
    </div>
  ) : null;

  const showAffiliation =
    view === 'list' &&
    (output.authors.length > 0 ||
      output.departments.length > 0 ||
      output.affiliations.length > 0 ||
      output.fieldsOfResearch.length > 0 ||
      output.sustainableDevelopmentGoals.length > 0 ||
      output.grants.length > 0 ||
      (output.researchers?.length > 0 && featureFlags.includes('search_by_researcher')));

  const isLibrarian = window.current_user.librarian;

  return (
    <div className="ResearchOutputItem" data-testid="research-output-item">
      <button onClick={onClick} className="output">
        <div className="badge">
          <img
            className="badge"
            loading="lazy"
            src={output.badgeURL}
            alt={I18n.t('Explorer.Outputs.ResearchOutputItem.alt', { score: output.score })}
          />
          {output.verified && (
            <div className="verified-badge" data-testid="verified-badge">
              <div
                className="icon"
                role="img"
                aria-label={I18n.t('Explorer.Outputs.ResearchOutputItem.verified', {
                  name: window.current_user.institution_name
                })}
                title={I18n.t('Explorer.Outputs.ResearchOutputItem.verified', {
                  name: window.current_user.institution_name
                })}
              />
            </div>
          )}
        </div>
        <div className="content">
          <h3 className="title">
            <PublicationStatusFlags output={output} />
            {outputTitle}
          </h3>
          <div className="meta">
            {output.meta}
            {sortContext}
          </div>
        </div>
      </button>
      {showAffiliation && (
        <div className="affiliation" data-testid="affiliation">
          {featureFlags.includes('search_by_researcher') &&
            !isLibrarian &&
            chipsSection('Researchers', output.researchers, (researcher) => (
              <Link
                key={researcher.dimensions_researcher_id}
                to={affiliationSearchLink({ 'researcher_id[]': [researcher.dimensions_researcher_id] })}
                className="chip"
              >
                {researcher.full_name}
              </Link>
            ))}
          {chipsSection('Authors', output.authors, (author) => (
            <Link key={author.id} to={affiliationSearchLink({ author_id: author.id })} className="chip">
              {author.name}
            </Link>
          ))}
          {chipsSection('Departments', output.departments, (department) => (
            <Link key={department.id} to={affiliationSearchLink({ department_id: department.id })} className="chip">
              {department.name}
            </Link>
          ))}
          {!isLibrarian &&
            chipsSection(
              'Affiliations',
              output.affiliations,
              (affiliation) => (
                <Link
                  key={affiliation.grid_id}
                  to={affiliationSearchLink({ 'affiliations[]': [affiliation.canonical_grid_id] })}
                  className="chip"
                >
                  {affiliation.name}
                </Link>
              ),
              { className: 'grid-affiliations', i18nKey: 'grid_affiliation_html' }
            )}
          {!isLibrarian &&
            chipsSection(
              'FieldsOfResearch',
              output.fieldsOfResearch,
              (fieldsOfResearch) => (
                <Link
                  key={fieldsOfResearch.code}
                  to={affiliationSearchLink({ 'field_of_research_codes[]': [fieldsOfResearch.code] })}
                  className="chip"
                >{`${fieldsOfResearch.code} ${fieldsOfResearch.name}`}</Link>
              ),
              { className: 'fields-of-research', i18nKey: 'fields_of_research_html' }
            )}
          {!isLibrarian &&
            chipsSection(
              'SustainableDevelopmentGoals',
              output.sustainableDevelopmentGoals,
              (sdg) => (
                <Link
                  key={sdg.code}
                  to={affiliationSearchLink({ 'sustainable_development_goals[]': [sdg.code] })}
                  className="chip"
                >
                  {`${sdg.code} ${sdg.name}`}
                </Link>
              ),
              { className: 'sustainable-development-goals', i18nKey: 'sustainable_development_goals_html' }
            )}
          {!isLibrarian &&
            chipsSection('Grants', output.grants, (grant) => (
              <Link
                key={grant.dimensions_grant_id}
                to={affiliationSearchLink({ 'grant_id[]': [grant.dimensionsGrantId] })}
                className="chip"
                data-tooltip-id="GrantInfo"
                data-tooltip-html={`<p>${grant.title}</p><p>${grant.funderName}</p>`}
              >
                {grant.projectNumbers}
              </Link>
            ))}
        </div>
      )}
    </div>
  );
};

ResearchOutputItem.propTypes = {
  onBadgeClick: PropTypes.func.isRequired,
  output: PropTypes.instanceOf(ResearchOutput).isRequired,
  featureFlags: PropTypes.array
};

export default withFeatureFlags(ResearchOutputItem);
